import {
  Download,
  ExternalLink,
} from 'lucide-react';
import React from 'react';
import './index.scss';
import { useReportsReadyForYouHook } from './useReportsReadyForYouHook';
import { ReportsFilesDownloadIcon, GridSkeleton } from 'Components/Grid';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import lab_profile_form from 'Assets/Icons/lab_profile_grid.svg';
import {
  PaginationFirstIcon,
  PaginationPrevIcon,
  PaginationNextIcon,
  PaginationLastIcon,
} from 'Components/Display/KendoIcons';

interface ReportsReadyForYouProps {
  clientId: string | null;
  clientName?: string | null;
}

export const ReportsReadyForYou = ({ clientId, clientName }: ReportsReadyForYouProps) => {
  const {
    closedCases,
    loading,
    page,
    totalPages,
    totalRecords,
    handlePrev,
    handleNext,
    handleFirst,
    handleLast,
    handlePageClick,
    navigateToClosedCasesPage,
    navigateToReferralDetailPage,
  } = useReportsReadyForYouHook({ clientId, clientName });

  return (
    <div className="card reports-table">
      <div className="section-header">
        <h2>Reports Ready for You</h2>
        <span className="view-all-link" onClick={navigateToClosedCasesPage}>
          View All
        </span>
      </div>

      <table>
        <thead>
          <tr>
            <th style={{ paddingLeft: '32px' }}>Claimant Name</th>
            <th>Stradix #</th>
            <th style={{ display: "flex", justifyContent: "center" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={3} style={{ padding: 0 }}>
                <GridSkeleton columns={3} rows={6} />
              </td>
            </tr>
          ) : closedCases.length === 0 ? (
            <tr>
              <td colSpan={3} style={{ textAlign: 'center', padding: '1rem' }}>
                <div
                  style={{
                    background: '#F6F7F9',
                    padding: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    color: '#546778',
                  }}
                >
                  <div style={{ width: '80px', height: '80px', backgroundColor: '#E0E4EB', borderRadius: '80px' }}>
                    <img
                      src={lab_profile_form}
                      alt="No records"
                      style={{ width: 50, marginBottom: 10, color: '#879AAB !important', paddingTop: '15px' }}
                    />
                  </div>
                  <span className="no-records-title">
                    {'Reports coming soon'}
                  </span>
                  <span className="no-records-description">
                    {
                      'Finalized versions will appear here when ready.'}
                  </span>
                </div>
              </td>
            </tr>
          ) : (
            closedCases.map((report, idx) => (
              <tr key={report.id}>
                <td onClick={() => navigateToReferralDetailPage(report.id)} style={{ paddingLeft: '32px' }}>
                  {report.claimantName}
                </td>
                <td onClick={() => navigateToReferralDetailPage(report.id)}>
                  {report.stradixNumber}
                </td>
                <td style={{ display: "flex", justifyContent: "center" }}>
                  <div className="reports-table-actions">
                    <button title="Download">
                      <ReportsFilesDownloadIcon
                        fileControllerEndpoint={`${ENDPOINTS.appointmentreports.base}/ClosedReferral`}
                        display={<Download size={18} />}
                        id={report.id}
                        filename={report.reportFileName}
                        dataItem={report}
                        dataIndex={idx}
                        ariaColumnIndex={3}
                        isSelected={false}
                        noLoadingLabel
                      />
                    </button>
                    {/* 
                    <button title="Open">
                      <ExternalLink size={18} />
                    </button> 
                    */}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="pagination">
        <button
          onClick={handleFirst}
          disabled={page === 1 || totalPages <= 1 || loading}
          title="First page"
        >
          <PaginationFirstIcon />
        </button>

        <button
          onClick={handlePrev}
          disabled={page === 1 || totalPages <= 1 || loading}
          title="Previous page"
        >
          <PaginationPrevIcon />
        </button>

        {Array.from({ length: totalPages || 1 }, (_, i) => i + 1).map((num) => (
          <button
            key={num}
            className={page === num ? 'active' : 'page-num'}
            onClick={() => handlePageClick(num)}
            disabled={totalPages <= 1 || loading}
          >
            {num}
          </button>
        ))}

        <button
          onClick={handleNext}
          disabled={page === totalPages || totalPages <= 1 || loading}
          title="Next page"
        >
          <PaginationNextIcon />
        </button>

        <button
          onClick={handleLast}
          disabled={page === totalPages || totalPages <= 1 || loading}
          title="Last page"
        >
          <PaginationLastIcon />
        </button>
      </div>
    </div>
  );
};
